import { NgClass } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";

import { MatButton } from "@angular/material/button";
import { NavigationEnd, Router, RouterLink, RouterOutlet } from "@angular/router";
import { LoaderComponent } from "@components/loader/loader.component";
import { MainNavComponent } from "@components/main-nav/main-nav.component";
import { FlexLayoutModule } from "@ngbracket/ngx-layout";
import { debounceTime, filter, fromEvent, Subject, Subscription, startWith, takeUntil } from "rxjs";
import { ImageUrlDirective } from "./directives";
import { ApiMessageService, AppStateService, RoutingStateService } from "./services";

@Component({
  selector: "fh-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.css"],
  standalone: true,
  imports: [
    MainNavComponent,
    FlexLayoutModule,
    NgClass,
    MatButton,
    RouterLink,
    RouterOutlet,
    LoaderComponent,
    ImageUrlDirective,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  resizeSubscription: Subscription | undefined;
  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    public apiMessageService: ApiMessageService,
    private readonly appStateService: AppStateService,
    private readonly router: Router,
    private readonly routingStateService: RoutingStateService
  ) {}

  ngOnInit() {
    // Start publishing NavigationEnd into AppStateService.routeNavigationEnd$.
    // Without this the subject never emits, so ApiMessageService could never
    // dismiss an alert on navigation and toasts lingered onto the next page.
    this.routingStateService.init();

    // Auth state (userToken$/currentUser$) was already established by
    // APP_INITIALIZER's bootstrapSession() call before this component ever
    // renders — no localStorage read needed. The bootstrap country fetch
    // needs a valid session, so it fails when the app loads at the login
    // screen and was never retried — leaving every country dropdown empty
    // for the whole session. Re-check on each navigation (no-op once loaded)
    // so the list self-heals after the user signs in.
    if (this.appStateService.userToken$.value) this.appStateService.ensureCountriesLoaded();
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.appStateService.userToken$.value) {
          this.appStateService.ensureCountriesLoaded();
        }
      });

    this.resizeSubscription = fromEvent(window, "resize")
      .pipe(startWith(null), debounceTime(200), takeUntil(this.destroy$))
      .subscribe(() => {
        this.appStateService.windowSize$.next({
          innerHeight: window.innerHeight,
          innerWidth: window.innerWidth,
        });

        this.appStateService.deviceType$.next(window.innerWidth < 768 ? "mobile" : "desktop");
      });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }
}
